import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import '@/lib/amplify';

export type SignInResult = { done: true } | { done: false; challenge: 'new-password' };

interface AuthState {
  status: 'loading' | 'signedIn' | 'signedOut';
  email?: string;
  userId?: string;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeNewPassword: (password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [email, setEmail] = useState<string>();
  const [userId, setUserId] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      // A user without valid tokens is not signed in, whatever the local cache says.
      if (!session.tokens?.accessToken) throw new Error('no tokens');
      setUserId(user.userId);
      setEmail((session.tokens.idToken?.payload.email as string | undefined) ?? user.username);
      setStatus('signedIn');
    } catch {
      setUserId(undefined);
      setEmail(undefined);
      setStatus('signedOut');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      email,
      userId,
      signIn: async (nextEmail, password) => {
        const res = await amplifySignIn({ username: nextEmail, password });
        if (res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          return { done: false, challenge: 'new-password' };
        }
        await refresh();
        return { done: true };
      },
      completeNewPassword: async (password) => {
        await confirmSignIn({ challengeResponse: password });
        await refresh();
        return { done: true };
      },
      signOut: async () => {
        await amplifySignOut();
        await refresh();
      },
    }),
    [status, email, userId, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
